import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { artistService } from '../services/apiService'
import type { Artist } from '../types'

interface ArtistFormProps {
  artist: Artist | null
  onClose: () => void
}

interface ArtistFormData {
  name: string
  bio: string
  imageUrl: string
  spotifyUrl: string
  instagramUrl: string
  facebookUrl: string
}

export default function ArtistForm({ artist, onClose }: ArtistFormProps) {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<ArtistFormData>({
    name: '',
    bio: '',
    imageUrl: '',
    spotifyUrl: '',
    instagramUrl: '',
    facebookUrl: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (artist) {
      setFormData({
        name: artist.name,
        bio: artist.bio,
        imageUrl: artist.imageUrl || '',
        spotifyUrl: artist.spotifyUrl || '',
        instagramUrl: artist.instagramUrl || '',
        facebookUrl: artist.facebookUrl || '',
      })
    }
  }, [artist])

  const createMutation = useMutation({
    mutationFn: artistService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artists'] })
      onClose()
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { errors?: Record<string, string[]> } } }
        if (axiosError.response?.data?.errors) {
          const validationErrors: Record<string, string> = {}
          Object.entries(axiosError.response.data.errors).forEach(([key, value]) => {
            validationErrors[key.toLowerCase()] = value[0]
          })
          setErrors(validationErrors)
        }
      }
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Artist> }) =>
      artistService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artists'] })
      onClose()
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { errors?: Record<string, string[]> } } }
        if (axiosError.response?.data?.errors) {
          const validationErrors: Record<string, string> = {}
          Object.entries(axiosError.response.data.errors).forEach(([key, value]) => {
            validationErrors[key.toLowerCase()] = value[0]
          })
          setErrors(validationErrors)
        }
      }
    },
  })

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.bio.trim()) {
      newErrors.bio = 'Bio is required'
    }

    if (formData.spotifyUrl && !isValidUrl(formData.spotifyUrl)) {
      newErrors.spotifyUrl = 'Invalid URL format'
    }

    if (formData.instagramUrl && !isValidUrl(formData.instagramUrl)) {
      newErrors.instagramUrl = 'Invalid URL format'
    }

    if (formData.facebookUrl && !isValidUrl(formData.facebookUrl)) {
      newErrors.facebookUrl = 'Invalid URL format'
    }

    if (formData.imageUrl && !isValidUrl(formData.imageUrl)) {
      newErrors.imageUrl = 'Invalid URL format'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const isValidUrl = (url: string) => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const submitData = {
      name: formData.name.trim(),
      bio: formData.bio.trim(),
      imageUrl: formData.imageUrl.trim() || undefined,
      spotifyUrl: formData.spotifyUrl.trim() || undefined,
      instagramUrl: formData.instagramUrl.trim() || undefined,
      facebookUrl: formData.facebookUrl.trim() || undefined,
    }

    if (artist) {
      updateMutation.mutate({ id: artist.id, data: submitData })
    } else {
      createMutation.mutate(submitData)
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-6">
            {artist ? 'Edit Artist' : 'Add New Artist'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isPending}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                Bio <span className="text-red-500">*</span>
              </label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.bio ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isPending}
              />
              {errors.bio && (
                <p className="mt-1 text-sm text-red-600">{errors.bio}</p>
              )}
            </div>

            <div>
              <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-700 mb-1">
                Image URL
              </label>
              <input
                type="text"
                id="imageUrl"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.imageUrl ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isPending}
              />
              {errors.imageUrl && (
                <p className="mt-1 text-sm text-red-600">{errors.imageUrl}</p>
              )}
            </div>

            <div>
              <label htmlFor="spotifyUrl" className="block text-sm font-medium text-gray-700 mb-1">
                Spotify URL
              </label>
              <input
                type="text"
                id="spotifyUrl"
                name="spotifyUrl"
                value={formData.spotifyUrl}
                onChange={handleChange}
                placeholder="https://open.spotify.com/artist/..."
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.spotifyUrl ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isPending}
              />
              {errors.spotifyUrl && (
                <p className="mt-1 text-sm text-red-600">{errors.spotifyUrl}</p>
              )}
            </div>

            <div>
              <label htmlFor="instagramUrl" className="block text-sm font-medium text-gray-700 mb-1">
                Instagram URL
              </label>
              <input
                type="text"
                id="instagramUrl"
                name="instagramUrl"
                value={formData.instagramUrl}
                onChange={handleChange}
                placeholder="https://instagram.com/..."
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.instagramUrl ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isPending}
              />
              {errors.instagramUrl && (
                <p className="mt-1 text-sm text-red-600">{errors.instagramUrl}</p>
              )}
            </div>

            <div>
              <label htmlFor="facebookUrl" className="block text-sm font-medium text-gray-700 mb-1">
                Facebook URL
              </label>
              <input
                type="text"
                id="facebookUrl"
                name="facebookUrl"
                value={formData.facebookUrl}
                onChange={handleChange}
                placeholder="https://facebook.com/..."
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.facebookUrl ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isPending}
              />
              {errors.facebookUrl && (
                <p className="mt-1 text-sm text-red-600">{errors.facebookUrl}</p>
              )}
            </div>

            {(createMutation.isError || updateMutation.isError) && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                Failed to save artist. Please try again.
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={isPending}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? 'Saving...' : artist ? 'Update Artist' : 'Create Artist'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isPending}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
